import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Plus, User, CalendarDays, Check, X, Coffee } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["Class Teacher", "Assistant", "Support Staff", "Admin", "Front Desk"];
const CLASSES = ["Playgroup", "Nursery", "LKG", "UKG"];

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [dateStr, setDateStr] = useState(new Date().toISOString().slice(0, 10));
  const [att, setAtt] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", role: "Class Teacher", phone: "", assigned_class: "", email: "" });

  const load = async () => {
    const [s, a] = await Promise.all([
      api.get("/staff"),
      api.get("/staff-attendance", { params: { date: dateStr } }),
    ]);
    setStaff(s.data);
    const map = {};
    a.data.forEach((r) => { map[r.staff_id] = r.status; });
    setAtt(map);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateStr]);

  const save = async () => {
    try {
      await api.post("/staff", { ...form, assigned_class: form.assigned_class || null });
      toast.success("Staff added");
      setOpen(false);
      setForm({ name: "", role: "Class Teacher", phone: "", assigned_class: "", email: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const mark = async (sid, status) => {
    setAtt({ ...att, [sid]: status });
    try {
      await api.post("/staff-attendance", { staff_id: sid, date: dateStr, status });
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black">Staff</h1>
          <p className="text-muted-foreground font-medium">The lovely people who make Kidzkulam run.</p>
        </div>
        <Button data-testid="add-staff-btn" onClick={() => setOpen(true)} className="rounded-full h-12 px-6 font-bold hover:-translate-y-0.5 hover:shadow-md transition-all">
          <Plus size={18} /> Add Staff
        </Button>
      </header>

      <div className="flex items-center gap-2 card-soft rounded-full px-4 py-2 w-fit">
        <CalendarDays size={18} className="text-primary" />
        <Input type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} className="border-0 h-9 p-0 focus-visible:ring-0 font-semibold w-36" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {staff.map((s) => {
          const st = att[s.id];
          return (
            <Card key={s.id} data-testid={`staff-${s.id}`} className="rounded-3xl p-5 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl chip-teal flex items-center justify-center">
                  <User size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold truncate">{s.name}</div>
                  <div className="text-xs text-muted-foreground">{s.role} {s.assigned_class ? `· ${s.assigned_class}` : ""}</div>
                </div>
              </div>
              <div className="text-sm text-muted-foreground">{s.phone}</div>
              <div className="flex gap-2">
                <button data-testid={`sp-${s.id}`} onClick={() => mark(s.id, "Present")} className={`tap-btn flex-1 flex items-center justify-center gap-1 ${st === "Present" ? "bg-primary text-primary-foreground" : "chip-green"}`}>
                  <Check size={14} /> Present
                </button>
                <button onClick={() => mark(s.id, "Leave")} className={`tap-btn flex-1 flex items-center justify-center gap-1 ${st === "Leave" ? "bg-secondary text-secondary-foreground" : "chip-amber"}`}>
                  <Coffee size={14} /> Leave
                </button>
                <button onClick={() => mark(s.id, "Absent")} className={`tap-btn flex-1 flex items-center justify-center gap-1 ${st === "Absent" ? "bg-destructive text-destructive-foreground" : "chip-pink"}`}>
                  <X size={14} /> Absent
                </button>
              </div>
            </Card>
          );
        })}
        {staff.length === 0 && <div className="col-span-full text-center py-12 text-muted-foreground">No staff yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle>Add Staff</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11 rounded-xl" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Assigned Class</Label>
                <Select value={form.assigned_class || "__none"} onValueChange={(v) => setForm({ ...form, assigned_class: v === "__none" ? "" : v })}>
                  <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— None —</SelectItem>
                    {CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-xl" /></div>
          </div>
          <DialogFooter><Button data-testid="save-staff" onClick={save} className="rounded-full">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
